import type { FunctionComponent } from 'react';
import React, { useEffect, useCallback } from 'react';
import { Button } from '@trello/nachos/button';
import { Analytics } from '@trello/atlassian-analytics';
import { showFlag } from '@trello/nachos/experimental-flags';
import { sendErrorEvent } from '@trello/error-reporting';
import {
  getNetworkError,
  MemberErrorExtensions,
} from '@trello/graphql-error-handling';
import { forTemplate, localizeErrorCode } from '@trello/i18n';
import type { PIIString } from '@trello/privacy';
import { ConfirmEmailModalTestIds } from '@trello/test-ids';
import { l } from 'app/scripts/lib/localize';
import { useCurrentBoardBackground } from 'app/src/components/BoardTheme';
import { useResendVerificationEmailMutation } from './ResendVerificationEmailMutation.generated';
import styles from './ConfirmEmailModal.less';

const format = forTemplate('confirm_email_modal', {
  shouldEscapeStrings: false,
});

export interface ConfirmEmailModalProps {
  idBoard: string;
  idOrganization: string;
  inviterName: PIIString;
  boardName: string;
  email: PIIString;
}

export const ConfirmEmailModal: FunctionComponent<ConfirmEmailModalProps> = ({
  idBoard,
  idOrganization,
  inviterName,
  boardName,
  email,
}) => {
  const [
    resendVerificationEmail,
    { loading },
  ] = useResendVerificationEmailMutation();
  const { backgroundTopColor } = useCurrentBoardBackground();

  useEffect(() => {
    Analytics.sendScreenEvent({
      name: 'confirmEmailModal',
      containers: {
        board: {
          id: idBoard,
        },
        organization: {
          id: idOrganization,
        },
      },
    });
  }, [idBoard, idOrganization]);

  const resendEmailOnClick = useCallback(async () => {
    Analytics.sendClickedLinkEvent({
      linkName: 'resendEmailLink',
      source: 'confirmEmailModal',
    });
    try {
      await resendVerificationEmail({
        variables: {
          email,
        },
      });
      showFlag({
        id: 'resendVerificationEmailSuccess',
        title: l('alerts.email sent'),
        appearance: 'success',
        isAutoDismiss: true,
      });
    } catch (err) {
      // @ts-expect-error
      const error = getNetworkError(err);
      let title = l('alerts.could not send email');
      if (error?.code === MemberErrorExtensions.BAD_EMAIL) {
        title = localizeErrorCode('email error', 'invalid email address');
      } else if (
        error?.code === MemberErrorExtensions.ALREADY_CONFIRMED_EMAIL
      ) {
        title = localizeErrorCode('email error', 'not found');
      } else {
        // @ts-expect-error
        sendErrorEvent(err, {
          tags: {
            ownershipArea: 'trello-smoothie',
            feature: 'Confirm Email Modal',
          },
        });
      }
      showFlag({
        id: 'resendVerificationEmailError',
        title,
        appearance: 'error',
        isAutoDismiss: true,
      });
    }
  }, [email, resendVerificationEmail]);

  return (
    <div
      className={styles.overlay}
      style={{
        background: backgroundTopColor
          ? `linear-gradient(#00000000 17.74%, ${backgroundTopColor} 81.87%)`
          : '',
      }}
      data-testid={ConfirmEmailModalTestIds.Overlay}
    >
      <div className={styles.confirmEmailModal}>
        <div className={styles.letterOpenImage}>
          <img
            src={require('resources/images/letter-open.svg')}
            alt=""
            role="presentation"
          />
        </div>
        <h2 className={styles.header}>
          {format('user-shared-board-with-you', { inviterName, boardName })}
        </h2>
        <p className={styles.subheader}>
          {format('but-before-you-can-join-youll-need-to-confirm')}
        </p>
        <div className={styles.ctaContainer}>
          <Button
            className={styles.button}
            appearance="default"
            isLoading={loading}
            onClick={resendEmailOnClick}
            aria-label="resend-email"
          >
            {format('resend-email')}
          </Button>
          <a
            className={styles.link}
            href="https://help.trello.com/article/716-not-receiving-confirmation-emails-or-password-reset-emails"
            target="_blank"
          >
            {format('didnt-receive-the-email')}
          </a>
        </div>
      </div>
    </div>
  );
};
